import DivShadow from "@components/layout/DivShadow";
import HStack from "@components/layout/HStack";
import SlideUpPanel from "@components/ui/SlideUpPanel";
import Status from "@components/ui/Status";
import Tooltip from "@components/ui/Tooltip";
import { useAppointmentStore, useAttendeesStore, useOutlookStore, useResourceStore } from "@shared/stores";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ModalShowAttendees from "../../meeting/components/ModalShowAttendees";
import ModalSuggetTime from "../../meeting/components/ModalSuggetTime";
import { EStatusResource } from "../../resources/services/types/enum";
import { AttendeeInfo } from "../../resources/services/types/attendeeEntity";
import { isAllDayMeeting, isMoreThanOneDayEvent, parseISOString } from "../../resources/ulti";
import { Loader } from "@components/ui/Loader";

const FindAvailableTime = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenSuggestTime, setIsOpenSuggestTime] = useState(false);
  const { startDateTimeZone, endDateTimeZone } = useAppointmentStore();
  const { recurringDates } = useOutlookStore();
  const { selectedResource } = useResourceStore();
  const { mainAttendee, requiredAttendees, optionalAttendees, isLoading: isLoadingAttendees } = useAttendeesStore();

  const [internalAttendees, setInternalAttendees] = useState<AttendeeInfo[]>([]);
  const isRecurring = useMemo(() => recurringDates.length > 0, [recurringDates]);
  const isAllDayEvent = useMemo(
    () => isAllDayMeeting(parseISOString(startDateTimeZone), parseISOString(endDateTimeZone)),
    [startDateTimeZone, endDateTimeZone]
  );
  const isActiveSuggestTime = useMemo(
    () =>
      internalAttendees.length > 0 &&
      !isRecurring &&
      !isMoreThanOneDayEvent(startDateTimeZone, endDateTimeZone) &&
      startDateTimeZone !== endDateTimeZone,
    [internalAttendees, isRecurring, startDateTimeZone, endDateTimeZone]
  );

  const attendees = useMemo(() => {
    return [...(requiredAttendees || []), ...(optionalAttendees || []), ...(mainAttendee ? [mainAttendee] : [])];
  }, [requiredAttendees, optionalAttendees, mainAttendee, startDateTimeZone, endDateTimeZone]);

  const totalAvailable = useMemo(() => {
    return attendees.filter(
      (attendee) =>
        attendee.status(startDateTimeZone, endDateTimeZone, isRecurring, isAllDayEvent) === EStatusResource.Available
    ).length;
  }, [attendees]);

  const totalUnavailable = useMemo(() => {
    return attendees.filter(
      (attendee) =>
        attendee.status(startDateTimeZone, endDateTimeZone, isRecurring, isAllDayEvent) === EStatusResource.Unavailable
    ).length;
  }, [attendees]);

  const totalUnknown = useMemo(() => {
    return attendees.filter(
      (attendee) =>
        attendee.status(startDateTimeZone, endDateTimeZone, isRecurring, isAllDayEvent) === EStatusResource.Disable
    ).length;
  }, [attendees]);

  useEffect(() => {
    if (isOpenSuggestTime) {
      setIsOpenSuggestTime(false);
    }
  }, [attendees, startDateTimeZone, endDateTimeZone, isRecurring, JSON.stringify(selectedResource)]);

  useEffect(() => {
    const attendees = [...(requiredAttendees || []), ...(optionalAttendees || [])];
    setInternalAttendees(
      attendees.filter(
        (attendee) =>
          attendee.status(startDateTimeZone, endDateTimeZone, isRecurring, isAllDayEvent) !== EStatusResource.Disable
      )
    );
  }, [
    requiredAttendees,
    optionalAttendees,
    mainAttendee,
    startDateTimeZone,
    endDateTimeZone,
    isRecurring,
    isAllDayEvent,
  ]);

  return attendees.length === 1 && attendees[0]?.email === mainAttendee?.email ? null : (
    <DivShadow className="bg-veryLightGray rounded-lg px-4 py-2 flex-shrink-0 dark:bg-charcoalGray">
      <HStack justifyContent="between">
        {isLoadingAttendees ? (
          <HStack className="rounded-[4px] px-2 h-[30px]"><Loader texts={[""]} /></HStack>
        ) : (
          <Tooltip
            message={`${t("Total {{total}} Attendees ", {
              total: attendees.length,
            })} \n ${t("{{internal}} of {{total}} internal can attend.", {
              internal: totalAvailable,
              total: totalAvailable + totalUnavailable,
            })} \n ${t("{{external}} external is unknown.", { external: totalUnknown })}`}
            position="top"
          >
            <HStack
              gap={2}
              className="rounded-[4px] px-2 h-[30px] cursor-pointer bg-extraLightGray hover:bg-softBlue dark:bg-deepGray dark:hover:bg-deepPrimaryBlue"
              onClick={() => setIsOpen(true)}
            >
              <HStack gap={3}>
                <HStack gap={1}>
                  <Status status={EStatusResource.Available} className="w-4 h-4" />
                  <p className="text-xs">{totalAvailable}</p>
                </HStack>
                <HStack gap={1}>
                  <Status status={EStatusResource.Unavailable} className="w-4 h-4" />
                  <p className="text-xs">{totalUnavailable}</p>
                </HStack>
                <HStack gap={1}>
                  <Status status={EStatusResource.Disable} className="w-4 h-4" />
                  <p className="text-xs">{totalUnknown}</p>
                </HStack>
              </HStack>
            </HStack>
          </Tooltip>
        )}
        {isActiveSuggestTime ? (
          <HStack
            className="rounded-[4px] px-2 h-[30px] cursor-pointer hover:text-darkPrimaryBlue hover:bg-softBlue dark:hover:bg-deepPrimaryBlue dark:hover:text-coolBlue"
            onClick={() => setIsOpenSuggestTime(true)}
          >
            <p className="text-sm font-semibold">{t("Find available time")}</p>
          </HStack>
        ) : null}
      </HStack>

      {/* Modal slide up */}
      <SlideUpPanel
        title={t("Attendees")}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        children={<ModalShowAttendees />}
      />

      <SlideUpPanel
        title={t("Attendees")}
        isOpen={isOpenSuggestTime}
        onClose={() => setIsOpenSuggestTime(false)}
        children={<ModalSuggetTime onClose={() => setIsOpenSuggestTime(false)} />}
      />
    </DivShadow>
  );
};

export default FindAvailableTime;
